/* eslint-disable no-plusplus */
import React from 'react';

import {
  Box,
  Button,
  Chip,
  Divider,
  FormControl,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Modal,
  OutlinedInput,
  Paper,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { tableCellClasses } from '@mui/material/TableCell';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Meteor } from 'meteor/meteor';
import { Roles } from 'meteor/alanning:roles';
import { isEmpty, map } from 'lodash';
import PropTypes from 'prop-types';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import EditIcon from '@mui/icons-material/Edit';
// import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
// import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import methodPromise from '../../../modules/method-promise-wrapper';
import handleErrorInClient from '../../../modules/handle-error-in-client';

const UserControlEditor = ({ initialSetting }) => {
  const [settings, setSettings] = React.useState(initialSetting);
  const [openModal, setOpenModal] = React.useState(false);
  const [prevSettings, setPrevSettings] = React.useState(initialSetting);
  const [editmode, setEditmode] = React.useState(false);
  const [openTutorial, setOpenTutorial] = React.useState(false);
  const userId = Meteor.userId();
  const isAdmin = Roles.userIsInRole(userId, 'admin');
  const allRoles = ['free-user', 'basic-user', 'premium-user', 'admin'];
  const allTypes = ['imagen', 'url', 'pdf'];

  const handleSubmit = async () => {
    try {
      const method = settings.value === true ? 'admin.addUserControl' : 'admin.updateUserControl';
      await methodPromise(method, prevSettings);
      setSettings(prevSettings);
      setEditmode(!editmode);
    } catch (error) {
      handleErrorInClient(error);
    }
  };

  const handleEditToggle = () => {
    setEditmode(!editmode);
  };

  const handleCancel = () => {
    setEditmode(false);
    setPrevSettings(settings);
  };

  const handleNumber = (value, variable, usuario) => {
    const updatedSetting = {
      ...prevSettings,
      [variable]: {
        ...prevSettings[variable],
        [usuario]: value
      },
      value: false
    };
    setPrevSettings(updatedSetting);
  };

  const StyledTableCell = styled(TableCell)(({ theme }) => ({
    [`&.${tableCellClasses.head}`]: {
      backgroundColor: '#757575',
      color: theme.palette.common.white
    },
    [`&.${tableCellClasses.body}`]: {
      fontSize: 14
    }
  }));

  const StyledTableRow = styled(TableRow)(({ theme }) => ({
    '&:nth-of-type(odd)': {
      backgroundColor: theme.palette.action.hover
    },
    // hide last border
    '&:last-child td, &:last-child th': {
      border: 0
    }
  }));

  const addTutorial = () => {
    const updatedSettings = { ...prevSettings };
    const idValue = Array.from({ length: 18 }, () => Math.random().toString(36)[2]).join('');
    const object = { description: '', title: 'title', _id: idValue, imagen: '', properties: [] };
    updatedSettings.tutorial.push(object);
    setPrevSettings(updatedSettings);
  };

  const handleChangeValue = (event, item, secondItem) => {
    const updatedSettings = { ...prevSettings };
    const idx = updatedSettings.tutorial.findIndex(element => element._id === item._id);
    updatedSettings.tutorial[idx].properties[secondItem.index].value = event.target.value;
    setPrevSettings(updatedSettings);
  };

  const handleChangeTitle = (event, item, secondItem) => {
    const updatedSettings = { ...prevSettings };
    const idx = updatedSettings.tutorial.findIndex(element => element._id === item._id);
    updatedSettings.tutorial[idx].properties[secondItem.index].title = event.target.value;
    setPrevSettings(updatedSettings);
  };

  const deleteTutorial = id => {
    const updatedSettings = { ...prevSettings };
    const idx = updatedSettings.tutorial.findIndex(element => element._id === id);
    updatedSettings.tutorial.splice(idx, 1);
    setPrevSettings(updatedSettings);
  };

  const deleteProperties = (tutorialIndex, indice) => {
    const updatedSettings = { ...prevSettings };

    const { properties } = updatedSettings.tutorial[tutorialIndex];

    for (let i = 0; i < properties.length; i++) {
      const item = properties[i];
      if (item.index === indice) {
        properties.splice(i, 1);
        i--;
      } else if (item.index > indice) {
        properties[i].index--;
      }
    }
    setPrevSettings(updatedSettings);
  };

  const renderSelect = (label, name, value, onChange) => (
    <Box display="row" fullWidth mb={2}>
      <FormControl fullWidth>
        <InputLabel>{label}</InputLabel>
        <Select
          label={label}
          labelId="demo-multiple-chip-label"
          size="small"
          multiple
          name={name}
          disabled={!editmode}
          value={isEmpty(value) ? [] : value}
          onChange={onChange}
          input={<OutlinedInput id="select" label={label} />}
          renderValue={selected => (
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
              {selected.map(item => (
                <Chip key={item} label={item} />
              ))}
            </Box>
          )}
        >
          {allRoles.map(item => (
            <MenuItem key={item} value={item}>
              {item}
            </MenuItem>
          ))}
        </Select>
      </FormControl>
    </Box>
  );

  return (
    <form
      onSubmit={e => {
        e.preventDefault();
        handleSubmit();
      }}
    >
      <Grid container spacing={3} mb={3}>
        <Grid item xs={12} md={6} lg={4}>
          {renderSelect(
            'Add layer by uploading geojson',
            'uploadLayer',
            prevSettings.uploadLayer,
            event => {
              const setting = {
                ...prevSettings,
                uploadLayer: event.target.value,
                value: false
              };
              setPrevSettings(setting);
            }
          )}
          {renderSelect(
            'Add map by uploading geojson',
            'uploadMap',
            prevSettings.uploadMap,
            event => {
              const setting = {
                ...prevSettings,
                uploadMap: event.target.value,
                value: false
              };
              setPrevSettings(setting);
            }
          )}
          {renderSelect(
            'Download geojson of layer',
            'downloadLayer',
            prevSettings.downloadLayer,
            event => {
              const setting = {
                ...prevSettings,
                downloadLayer: event.target.value,
                value: false
              };
              setPrevSettings(setting);
            }
          )}
          {renderSelect(
            'Download geojson of map',
            'downloadMap',
            prevSettings.downloadMap,
            event => {
              const setting = {
                ...prevSettings,
                downloadMap: event.target.value,
                value: false
              };
              setPrevSettings(setting);
            }
          )}
          {renderSelect('Has Trash', 'hasTrash', prevSettings.hasTrash, event => {
            const setting = {
              ...prevSettings,
              hasTrash: event.target.value,
              value: false
            };
            setPrevSettings(setting);
          })}
          <Box display="row" fullWidth mb={2}>
            <FormControl fullWidth>
              <InputLabel>Rol por defecto</InputLabel>
              <Select
                label="Rol por defecto"
                size="small"
                name="defaultUserRole"
                disabled={!editmode}
                value={isEmpty(prevSettings.defaultUserRole) ? [] : prevSettings.defaultUserRole}
                onChange={event => {
                  const setting = {
                    ...prevSettings,
                    defaultUserRole: event.target.value,
                    value: false
                  };
                  setPrevSettings(setting);
                }}
                input={<OutlinedInput id="select" label="Rol por defecto" />}
              >
                {allRoles.map(item => (
                  <MenuItem key={item} value={item}>
                    {item}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
          <Box display="row" justifyContent="space-evenly" fullWidth mb={2}>
            <Typography>Número de layers:</Typography>
            <TextField
              disabled={!editmode}
              id="outlined"
              InputLabelProps={{ shrink: true }}
              label="Free-user"
              onChange={event => {
                const number = isEmpty(event.target.value) ? '' : parseInt(event.target.value, 10);
                handleNumber(number, 'numberLayersCreated', 'free');
              }}
              size="small"
              value={
                prevSettings &&
                prevSettings.numberLayersCreated &&
                prevSettings.numberLayersCreated.free !== undefined &&
                prevSettings.numberLayersCreated.free
              }
            />
            <TextField
              disabled={!editmode}
              id="outlined-required"
              InputLabelProps={{ shrink: true }}
              label="Basic-user"
              onChange={event => {
                const number = isEmpty(event.target.value) ? '' : parseInt(event.target.value, 10);
                handleNumber(number, 'numberLayersCreated', 'basic');
              }}
              size="small"
              value={
                prevSettings &&
                prevSettings.numberLayersCreated &&
                prevSettings.numberLayersCreated.basic !== undefined &&
                prevSettings.numberLayersCreated.basic
              }
            />
          </Box>
          <Box display="row" justifyContent="space-evenly" fullWidth mb={2}>
            <Typography>Número de mapas:</Typography>
            <TextField
              disabled={!editmode}
              id="outlined-required"
              InputLabelProps={{ shrink: true }}
              label="Free-user"
              onChange={event => {
                const number = isEmpty(event.target.value) ? '' : parseInt(event.target.value, 10);
                handleNumber(number, 'numberMapsCreated', 'free');
              }}
              size="small"
              value={
                prevSettings &&
                prevSettings.numberMapsCreated &&
                prevSettings.numberMapsCreated.free !== undefined &&
                prevSettings.numberMapsCreated.free
              }
            />
            <TextField
              disabled={!editmode}
              id="outlined-required"
              InputLabelProps={{ shrink: true }}
              label="Basic-user"
              onChange={event => {
                const number = isEmpty(event.target.value) ? '' : parseInt(event.target.value, 10);
                handleNumber(number, 'numberMapsCreated', 'basic');
              }}
              size="small"
              value={
                prevSettings &&
                prevSettings.numberMapsCreated &&
                prevSettings.numberMapsCreated.basic !== undefined &&
                prevSettings.numberMapsCreated.basic
              }
            />
          </Box>
        </Grid>

        <Grid item xs={12} md={6} lg={8}>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              mb: 2
            }}
            fullWidth
          >
            <Grid container spacing={2} alignItems="center">
              <Grid item>
                <IconButton
                  onClick={() => {
                    setOpenTutorial(!openTutorial);
                  }}
                >
                  {openTutorial === true ? <ExpandMoreIcon /> : <ExpandLessIcon />}
                </IconButton>
              </Grid>
              <Grid item>
                <Typography variant="body1">Página de inicio</Typography>
              </Grid>
              <Grid item xs>
                <Divider sx={{ height: 1, backgroundColor: 'grey' }} />
              </Grid>
            </Grid>
          </Box>
          {!isEmpty(prevSettings.tutorial) && openTutorial === true && (
            <>
              {map(prevSettings.tutorial, item => {
                return (
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      mb: 2,
                      width: '100%'
                    }}
                  >
                    <Paper sx={{ overflow: 'hidden', width: '100%' }}>
                      <TableContainer component={Paper} sx={{ maxHeight: 530 }} fullWidth>
                        <Table aria-label="customized table" size="small" stickyHeader>
                          <TableHead>
                            <TableRow>
                              <StyledTableCell align="left">{item.title}</StyledTableCell>
                              <StyledTableCell />
                              <StyledTableCell />
                              <StyledTableCell align="right">
                                <IconButton
                                  disabled={!editmode}
                                  onClick={() => {
                                    const idx = prevSettings.tutorial.findIndex(
                                      element => element._id === item._id
                                    );
                                    const updatedSettings = { ...prevSettings };
                                    const newObject = {
                                      value: '',
                                      type: 'url',
                                      index: prevSettings.tutorial[idx].properties.length
                                    };
                                    updatedSettings.tutorial[idx].properties.push(newObject);
                                    setPrevSettings(updatedSettings);
                                  }}
                                  sx={{
                                    color: '#2A3F90'
                                  }}
                                >
                                  <AddIcon />
                                </IconButton>
                                <IconButton
                                  disabled={!editmode}
                                  onClick={() => {
                                    setOpenModal(true);
                                  }}
                                  sx={{
                                    color: '#2A3F90'
                                  }}
                                >
                                  <EditIcon />
                                </IconButton>
                                <IconButton
                                  disabled={!editmode}
                                  onClick={() => {
                                    deleteTutorial(item._id);
                                  }}
                                  sx={{
                                    color: '#2A3F90'
                                  }}
                                >
                                  <DeleteIcon />
                                </IconButton>
                              </StyledTableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            <Modal
                              open={openModal}
                              item={item}
                              disableBackdropClick
                              style={{
                                position: 'absolute',
                                bgcolor: 'white',
                                top: '25vh',
                                left: '25vw'
                              }}
                            >
                              <Box backgroundColor="white" width="50vw" height="50vh">
                                <Box
                                  display="flex"
                                  height="10%"
                                  width="100%"
                                  alignItems="center"
                                  mr={5}
                                >
                                  <Grid
                                    container
                                    direction="row"
                                    justifyContent="space-around"
                                    spacing={3}
                                  >
                                    <Grid
                                      item
                                      xs={10}
                                      sx={{ justifyContent: 'center', display: 'flex' }}
                                    >
                                      <Box sx={{ width: '90%' }} />
                                    </Grid>
                                    <Grid
                                      item
                                      xs={2}
                                      sx={{ justifyContent: 'space-around', display: 'flex' }}
                                    >
                                      <Button
                                        color="success"
                                        variant="outlined"
                                        onClick={() => setOpenModal(false)}
                                      >
                                        Close
                                      </Button>
                                    </Grid>
                                  </Grid>
                                </Box>
                                <Box height="85%" width="80%" m={3} display="column">
                                  <TextField
                                    id="outlined-start-adornment"
                                    fullWidth
                                    label="Title"
                                    size="small"
                                    onChange={event => {
                                      const updatedSettings = { ...prevSettings };
                                      const idx = updatedSettings.tutorial.findIndex(
                                        element => element._id === item._id
                                      );
                                      updatedSettings.tutorial[idx].title = event.target.value;
                                      setPrevSettings(updatedSettings);
                                    }}
                                    value={isEmpty(item.title) ? '' : item.title}
                                  />
                                  <TextField
                                    id="outlined-start-adornment"
                                    fullWidth
                                    label="Picture"
                                    size="small"
                                    onChange={event => {
                                      const updatedSettings = { ...prevSettings };
                                      const idx = updatedSettings.tutorial.findIndex(
                                        element => element._id === item._id
                                      );
                                      updatedSettings.tutorial[idx].imagen = event.target.value;
                                      setPrevSettings(updatedSettings);
                                    }}
                                    value={isEmpty(item.imagen) ? '' : item.imagen}
                                  />
                                  <TextField
                                    id="outlined-start-adornment"
                                    label="Description"
                                    fullWidth
                                    size="small"
                                    onChange={event => {
                                      const updatedSettings = { ...prevSettings };
                                      const idx = updatedSettings.tutorial.findIndex(
                                        element => element._id === item._id
                                      );
                                      updatedSettings.tutorial[idx].description =
                                        event.target.value;
                                      setPrevSettings(updatedSettings);
                                    }}
                                    value={isEmpty(item.description) ? '' : item.description}
                                  />
                                </Box>
                              </Box>
                            </Modal>
                            {map(item.properties, secondItem => {
                              return (
                                <StyledTableRow key={secondItem._id}>
                                  <StyledTableCell component="th" scope="row">
                                    {editmode === true ? (
                                      <Select
                                        labelId="demo-select-label"
                                        id="demo-select"
                                        size="small"
                                        label="Sistema de coordenadas"
                                        value={isEmpty(secondItem.type) ? '' : secondItem.type}
                                        onChange={async event => {
                                          const updatedSettings = { ...prevSettings };
                                          const idx = updatedSettings.tutorial.findIndex(
                                            element => element._id === item._id
                                          );
                                          updatedSettings.tutorial[idx].properties[
                                            secondItem.index
                                          ].type = event.target.value;

                                          setPrevSettings(updatedSettings);
                                        }}
                                        disabled={!editmode}
                                      >
                                        {allTypes.map(option => (
                                          <MenuItem key={option} value={option}>
                                            {option}
                                          </MenuItem>
                                        ))}
                                      </Select>
                                    ) : (
                                      secondItem.type
                                    )}
                                  </StyledTableCell>
                                  <StyledTableCell component="th" scope="row">
                                    {editmode === true ? (
                                      <TextField
                                        id="outlined-start-adornment"
                                        fullWidth
                                        size="small"
                                        onChange={event =>
                                          handleChangeValue(event, item, secondItem)
                                        }
                                        value={isEmpty(secondItem.value) ? '' : secondItem.value}
                                      />
                                    ) : (
                                      secondItem.value
                                    )}
                                  </StyledTableCell>
                                  <StyledTableCell component="th" scope="row">
                                    {editmode === true ? (
                                      <TextField
                                        id="outlined-start-adornment"
                                        fullWidth
                                        size="small"
                                        onChange={event =>
                                          handleChangeTitle(event, item, secondItem)
                                        }
                                        value={isEmpty(secondItem.title) ? '' : secondItem.title}
                                      />
                                    ) : (
                                      secondItem.title
                                    )}
                                  </StyledTableCell>

                                  <StyledTableCell align="right">
                                    <Stack
                                      color="inherit"
                                      size="small"
                                      direction="row"
                                      justifyContent="flex-end"
                                      alignItems="center"
                                      spacing={0}
                                      disabled={!editmode}
                                    >
                                      <IconButton
                                        disabled={!editmode}
                                        onClick={() => {
                                          const idx = prevSettings.tutorial.findIndex(
                                            element => element._id === item._id
                                          );
                                          deleteProperties(idx, secondItem.index);
                                        }}
                                        sx={{
                                          color: '#2A3F90'
                                        }}
                                      >
                                        <DeleteIcon />
                                      </IconButton>

                                      {/* <IconButton
                                    edge="end"
                                    // onClick={() => moveLayerUpFirst(_id)}
                                    aria-label="comments"
                                  >
                                    <KeyboardDoubleArrowUpIcon />
                                  </IconButton> */}
                                      {/* <IconButton
                                        edge="end"
                                        // onClick={() => moveLayerUp(_id)}
                                        aria-label="comments"
                                      >
                                        <KeyboardArrowUpIcon />
                                      </IconButton>
                                      <IconButton
                                        edge="end"
                                        // onClick={() => moveLayerDown(_id)}
                                        aria-label="comments"
                                      >
                                        <KeyboardArrowDownIcon />
                                      </IconButton> */}
                                    </Stack>
                                  </StyledTableCell>
                                </StyledTableRow>
                              );
                            })}
                          </TableBody>
                        </Table>
                      </TableContainer>
                    </Paper>
                  </Box>
                );
              })}
            </>
          )}
          <Button disabled={!editmode} variant="contained" color="success" onClick={addTutorial}>
            <FontAwesomeIcon icon="save" /> Añadir tutorial
          </Button>
        </Grid>
      </Grid>
      {isAdmin && (
        <Grid container spacing={3} justifyContent="flex-start">
          <Grid item xs={12} md={6} lg={4}>
            <Button
              disabled={editmode}
              variant="contained"
              color="primary"
              onClick={handleEditToggle}
            >
              <FontAwesomeIcon icon="edit" size="lg" />{' '}
              {settings.value ? 'Add Setting' : 'Edit Setting'}
            </Button>
            <Button disabled={!editmode} type="submit" variant="contained" color="success">
              <FontAwesomeIcon icon="save" size="lg" /> Save
            </Button>
            <Button disabled={!editmode} variant="contained" color="error" onClick={handleCancel}>
              <FontAwesomeIcon icon="times" size="lg" /> Cancel
            </Button>
          </Grid>
        </Grid>
      )}

      <br />
    </form>
  );
};
UserControlEditor.propTypes = {
  initialSetting: PropTypes.object.isRequired
};

export default UserControlEditor;
